import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { createApiClient } from '@n8n-builder/api-client';
import { useCreditsStore } from '../store/credits';
import { API_BASE_URL } from '../lib/api-config';
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function useCredits() {
  const { isAuthenticated } = useAuth();
  const {
    credits,
    monthly,
    used,
    subscription,
    isLoading,
    error,
    setCredits,
    decrementCredits,
    setLoading,
    setError,
    reset,
  } = useCreditsStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!isAuthenticated) {
      reset();
      return;
    }

    setLoading(true);

    try {
      const apiClient = createApiClient(API_BASE_URL);
      // Better Auth uses cookies, no token needed

      const result = await apiClient.getCredits();

      if (result.error) {
        setError(result.error);
      } else if (result.data) {
        setCredits(result.data.credits, result.data.monthly, result.data.used, result.data.subscription, result.data.maxCredits);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, setCredits, setError, setLoading, reset]);

  // Fetch credits on mount and when sign-in state changes
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Set up auto-refresh interval
  useEffect(() => {
    if (isAuthenticated) {
      intervalRef.current = setInterval(fetchCredits, REFRESH_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, fetchCredits]);

  return {
    credits,
    monthly,
    used,
    subscription,
    isLoading,
    error,
    refetch: fetchCredits,
    decrementCredits,
  };
}
